/**
 * Lightweight time-scale modulator triggered on local kills. Used by
 * GameClient to slow simulation deltaMs for ~100ms post-kill.
 */
export class HitstopController {
  /** Active hit-stop window in ms; counts down via {@link update}. */
  private remainingMs = 0;
  /** Time scale to return while the window is active (1 = no slowdown). */
  private activeScale = 1.0;

  /**
   * Begin a hit-stop window of `durationMs` during which {@link currentTimeScale}
   * returns `scale`. A re-trigger before the previous window expires takes
   * the longer of the two remaining durations and the latest requested scale.
   */
  triggerHitstop(durationMs: number = 100, scale: number = 0.05): void {
    if (durationMs <= 0) return;
    if (durationMs > this.remainingMs) this.remainingMs = durationMs;
    this.activeScale = scale;
  }

  /**
   * Frame-time multiplier for the rest of the client render loop. Returns
   * `scale` (default 0.05) while a window is active, else 1.0.
   */
  currentTimeScale(): number {
    return this.remainingMs > 0 ? this.activeScale : 1.0;
  }

  /** Decrement the active window by `deltaMs`, clamped at 0. */
  update(deltaMs: number): void {
    if (this.remainingMs <= 0) return;
    this.remainingMs = Math.max(0, this.remainingMs - deltaMs);
  }

  /** Test/diagnostic helper: returns the unconsumed window length in ms. */
  remaining(): number {
    return this.remainingMs;
  }
}
